import { createAsyncThunk } from "@reduxjs/toolkit";
import { reportGeneratorListing, reportTableGenerator } from "../service";
import { getStorage } from "../../../../utilities/browserStorage";
import { onResetReportFilter } from "./reportGeneratorSlice";
import i18n from "../../../../utilities/i18n";

/**
 * 
 * Thunk to fetch report data using given payload.
 * Returns results, count, and page size on success.
 * Logs and rethrows errors on failure.
 */
export const fetchReportData = createAsyncThunk(
    "reportGenerator/fetchReportData",
    async (payload) => {
        try {
            const response = await reportGeneratorListing(payload);
            const status = response.status;
            const data = response?.data;

            if (status == 200 ){
                return {
                    data: data.results,
                    count: data.count,
                    page_size: data.page_size,
                }
            }
        }
            catch (error) {
            console.error(error,"ERR");
            throw error;  
        }
    }
)
export const buildFiltersUsed = (activeFilters, practicePK) => {
  let query = {};
  
  const filtersMap = {
    date_of_service_from: activeFilters.date_of_service_from,
    date_of_service_to: activeFilters.date_of_service_to,
    date_of_entry_from: activeFilters.date_of_entry_from,
    date_of_entry_to: activeFilters.date_of_entry_to,
    first_billed_date_from: activeFilters.first_billed_date_from,
    first_billed_date_to: activeFilters.first_billed_date_to,
    last_billed_date_from: activeFilters.last_billed_date_from,
    last_billed_date_to: activeFilters.last_billed_date_to,
    date_of_payment_entry_from: activeFilters.date_of_payment_entry_from,
    date_of_payment_entry_to: activeFilters.date_of_payment_entry_to,
    sample_received_date_from: activeFilters.sample_received_date_from,
    sample_received_date_to: activeFilters.sample_received_date_to,
    icd: activeFilters.icd,
    patient_ids: activeFilters.patient_ids?.map(item => item.id),
    insurance_company: activeFilters.insurance_company,
    payer_type: activeFilters.payer_type,
    reffering_provider: activeFilters.reffering_provider,
    rendering_provider: activeFilters.rendering_provider,
    billing_provider: activeFilters.billing_provider,
    service_locations: activeFilters.service_locations,
    transaction_entered_by_user: activeFilters.transaction_entered_by_user?.map(id => Number(id)),
    transaction_from: activeFilters.transaction_from,
    transaction_to: activeFilters.transaction_to,
    transaction_type: activeFilters.transaction_type,
    aging: activeFilters.aging,
    balance_type: activeFilters.balance_type,
    balance_amount_start: activeFilters.balance_amount_start,
    balance_amount_end: activeFilters.balance_amount_end,
    cpt_code: activeFilters.cpt_code,
    claim_status: activeFilters.claim_status,
    claim_sub_status: activeFilters.claim_sub_status,
    payment_enter_user: activeFilters.payment_enter_user,
    charge_enter_user: activeFilters.charge_enter_user,
    payment_check_number: activeFilters.payment_check_number,
    payment_check_date_from: activeFilters.payment_check_date_from,
    payment_check_date_to: activeFilters.payment_check_date_to,
    payment_entered_date_from: activeFilters.payment_entered_date_from,
    payment_entered_date_to: activeFilters.payment_entered_date_to,
    payment_modes: activeFilters.payment_modes,
    insurance_payer_ids: activeFilters.insurance_payer_ids?.map(item => Number(item.split('-')[1])),
    adjustment_type_ids: activeFilters.adjustment_type_ids,
    level_adjustment_condition: activeFilters.level_adjustment_condition,
    level_adjustment_start: activeFilters.level_adjustment_start,
    level_adjustment_end: activeFilters.level_adjustment_end,
    applied_amount_condition: activeFilters.applied_amount_condition,
    applied_amount_start: activeFilters.applied_amount_start,
    applied_amount_end: activeFilters.applied_amount_end,
    unapplied_amount_condition: activeFilters.unapplied_amount_condition,
    unapplied_amount_start: activeFilters.unapplied_amount_start,
    unapplied_amount_end: activeFilters.unapplied_amount_end,
    practice_pk: practicePK,
    lab_sales_rep: activeFilters.lab_sales_rep,
    clinic_site_name: activeFilters.clinic_site_name,
    claim_service_type: activeFilters.claim_service_type,
    charge_amount_condition: activeFilters.charge_amount_condition,
    charge_amount_start: activeFilters.charge_amount_start,
    charge_amount_end: activeFilters.charge_amount_end,
    valid_auth_from: activeFilters.valid_auth_from,
    valid_auth_to: activeFilters.valid_auth_to,
    visit_remaining_min: activeFilters.visit_remaining_min,
    visit_remaining_max: activeFilters.visit_remaining_max,
    secondary_payer_ids:activeFilters.secondary_payer_ids?.map(item => Number(item.split('-')[1])),
    primary_payer_ids:activeFilters.primary_payer_ids?.map(item => Number(item.split('-')[1])),
    action_date_from: activeFilters.action_date_from,
    action_date_to: activeFilters.action_date_to,
    users: activeFilters.users,
  };

  // loop through and include only truthy and non-empty values
  Object.entries(filtersMap).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      query[key] = value;
    }
  });

  return query;
};

/**
 * Thunk to generate a report using selected filters and practice data.
 * On success, triggers report fetch and resets applied filters.
 */
export const generateReportData = createAsyncThunk(
    "reportGenerator/generateReportData",
    async (payload, thunkAPI) => {
        const state = thunkAPI.getState().ReportGeneratorData;
        const practicePK = getStorage("practice");
        const { reportType, search_key, to_date, from_date, report_status, reportTypeName, activeFilters, activePage } = state;                
        
        try {
            const filters_used = buildFiltersUsed(activeFilters, practicePK);
            let data = {
                practice: practicePK,
                report_type: reportType,
                filters_used
            }
            const response =  await reportTableGenerator(data);
            const status = response?.status;
            const results = response?.data;

            if (status === 201 && results){
                thunkAPI.dispatch(fetchReportData({
                    practice_id: practicePK,
                    reference_id: search_key,
                    status: report_status,
                    from_date: from_date,
                    to_date: to_date,
                    page:activePage
                }))
                thunkAPI.dispatch(onResetReportFilter(reportTypeName));
                return {
                    data: results,
                    message: `Report generated successfully with reference id ${results.reference_id}`,
                    statusCode: "success"
                }
            } else if (results.message === "practice_not_found") {
                return {
                    data: "",
                    message: "Practice not found",
                    statusCode: "error"
                }
            } else if (results.message === 'database_error') {
                return {
                    data: "",
                    message: "Data base error",
                    statusCode: "error"
                }
            } 
            else if (results.message === 'celery_workers_are_not_runing' || results.message === "S3_upload_error") {
                return {
                    data: "",
                    message: i18n.t("errorMessages.commonError"),
                    statusCode: "error"
                }
            } 
            else if (results.message === 'excel_generation_failed') {
                return {
                    data: "",
                    message: "Excel generation failed.",
                    statusCode: "error"
                }
            } else if (results.message === 'report_type_not_found') {
                return {
                    data: "",
                    message: "Report type not found.",
                    statusCode: "error"
                }
            }
            else
            {
                return thunkAPI.rejectWithValue({
                    message: "Failed to generate report.",
                    statusCode: "error"
                });
            }
            
        } catch (error) {
            return thunkAPI.rejectWithValue({
                message: error?.message || "Unexpected error occurred."
            });
        }
    }
)
